import { useEffect, useRef } from 'react';
import { css } from '@emotion/react';
import { i18next, useTranslation } from '@frontend/i18n';
import { ContainedIcon, Icon, IconName } from '@frontend/icons';
import { AiContainedIcon, gradientAnimationStyles } from '@frontend/styled-icons';
import { theme } from '@frontend/theme';
import { Avatar, BannerNotification, Button, Text } from '@frontend/design-system';

export type ScheduleActionType = 'SCHEDULE' | 'RESCHEDULE' | 'CANCEL' | 'CONFIRM' | 'REQUEST';

export interface TranscriptTurn {
  id: string;
  speaker: 'user' | 'ai';
  text: string;
  timestamp: Date;
  isPartial?: boolean;
}

export interface ScheduleEventTurn {
  id: string;
  type: 'schedule_event';
  actionType: ScheduleActionType;
  timestamp: Date;
}

export type ConversationTurn = TranscriptTurn | ScheduleEventTurn;

interface ConversationTranscriptProps {
  transcripts: ConversationTurn[];
  userInitials?: string;
  userName?: string;
  isAISpeaking?: boolean;
  isUserSpeaking?: boolean;
  showYourTurn?: boolean;
  callEnded?: boolean;
  settingsChangedBanner?: string | null;
  onViewCalendar?: () => void;
}

interface TurnCardProps {
  turn: TranscriptTurn;
  userInitials?: string;
  userName?: string;
}

const isScheduleEventTurn = (turn: ConversationTurn): turn is ScheduleEventTurn => {
  return 'type' in turn && turn.type === 'schedule_event';
};

const containerStyle = css`
  padding: ${theme.spacing(2)};
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing(2)};
`;

const turnContentStyle = css`
  display: grid;
  grid-template-columns: auto 1fr;
  align-items: center;
  padding: ${theme.spacing(2)};
  gap: ${theme.spacing(1)};
  box-shadow: ${theme.shadows.light};
  border-radius: ${theme.borderRadius.medium};
`;

const scheduleEventCardStyle = css`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: var(--fabric-space-padding-sm) var(--fabric-space-padding-md);
  border-radius: var(--fabric-radius-md);
  background: var(--fabric-color-surface-canvas);
  box-shadow: ${theme.shadows.light};
`;

const scheduleEventLeftContentStyle = css`
  display: flex;
  align-items: center;
  gap: var(--fabric-space-gap-xs);
`;

const typingIndicatorStyle = css`
  display: inline-flex;
  gap: 4px;

  span {
    animation: bounce 1.4s infinite ease-in-out;
    display: inline-block;

    &:nth-of-type(1) {
      animation-delay: 0s;
    }

    &:nth-of-type(2) {
      animation-delay: 0.2s;
    }

    &:nth-of-type(3) {
      animation-delay: 0.4s;
    }
  }

  @keyframes bounce {
    0%,
    60%,
    100% {
      transform: translateY(0);
    }
    30% {
      transform: translateY(-8px);
    }
  }
`;

const AITurnCard = ({ turn }: { turn: TranscriptTurn }) => {
  return (
    <div css={turnContentStyle}>
      <AiContainedIcon iconName='aiweave-assistant' size={40} />
      <Text>{turn.text}</Text>
    </div>
  );
};

const AITypingIndicator = () => {
  return (
    <div
      css={[
        css`
          box-shadow: ${theme.shadows.light};
          border-radius: ${theme.borderRadius.medium};
        `,
        {
          border: '1px solid transparent',
          background: `
          linear-gradient(${theme.colors.white}, ${theme.colors.white}) padding-box,
          linear-gradient(90deg, #1950A3 0%, #0D5D59 100%) border-box
        `,
        },
      ]}
    >
      <div css={[turnContentStyle, gradientAnimationStyles()]}>
        <ContainedIcon
          iconName='aiweave-assistant'
          size={40}
          css={{
            padding: 2,
            borderRadius: theme.borderRadius.small,
          }}
        />
        <div css={typingIndicatorStyle}>
          <Text as='span' color={'light'}>
            ●
          </Text>
          <Text as='span' color={'light'}>
            ●
          </Text>
          <Text as='span' color={'light'}>
            ●
          </Text>
        </div>
      </div>
    </div>
  );
};

const UserTypingIndicator = () => {
  return (
    <div
      css={[
        turnContentStyle,
        {
          border: `1px solid ${theme.colorsV2.blue50()}`,
        },
      ]}
    >
      <Avatar size='medium' />
      <div css={typingIndicatorStyle}>
        <Text as='span' color={'primary'}>
          ●
        </Text>
        <Text as='span' color={'primary'}>
          ●
        </Text>
        <Text as='span' color={'primary'}>
          ●
        </Text>
      </div>
    </div>
  );
};

const YourTurnIndicator = () => {
  const { t } = useTranslation('ai-receptionist');
  return (
    <div css={turnContentStyle}>
      <Avatar size='medium' />
      <Text color={'light'}>{t('Ask Sam anything...')}</Text>
    </div>
  );
};

const CallEndedIndicator = () => {
  return (
    <div
      css={css`
        display: flex;
        align-items: center;
        gap: ${theme.spacing(2)};

        &::before,
        &::after {
          content: '';
          flex: 1;
          height: 1px;
          background-color: ${theme.colorsV2.neutral30()};
        }
      `}
    >
      <Text color='subdued' css={{ whiteSpace: 'nowrap' }}>
        Call Ended
      </Text>
    </div>
  );
};

const getScheduleCardMetadata = (actionType: ScheduleActionType) => {
  switch (actionType) {
    case 'RESCHEDULE':
      return {
        iconName: 'calendar-rescheduled' as IconName,
        iconColor: 'var(--fabric-color-accent-eggplant-base)',
        title: i18next.t('Appointment Rescheduled', { ns: 'ai-receptionist' }),
      };
    case 'CANCEL':
      return {
        iconName: 'calendar-canceled' as IconName,
        iconColor: 'var(--fabric-color-status-error-default)',
        title: i18next.t('Removed from Schedule', { ns: 'ai-receptionist' }),
      };
    case 'SCHEDULE':
    case 'REQUEST':
    case 'CONFIRM':
    default:
      return {
        iconName: 'calendar-add' as IconName,
        iconColor: 'var(--fabric-color-accent-blue-base)',
        title: i18next.t('Added to Schedule', { ns: 'ai-receptionist' }),
      };
  }
};

export const ScheduleEventCard = ({
  turn,
  onViewCalendar,
}: {
  turn: ScheduleEventTurn;
  onViewCalendar?: () => void;
}) => {
  const { title, iconName, iconColor } = getScheduleCardMetadata(turn.actionType);
  const { t } = useTranslation('ai-receptionist');

  return (
    <div css={scheduleEventCardStyle}>
      <div css={scheduleEventLeftContentStyle}>
        <Icon name={iconName} size={24} css={{ color: iconColor }} />
        <Text>{title}</Text>
      </div>
      <Button variant='tertiary' size='small' onClick={onViewCalendar} disabled={!onViewCalendar}>
        {t('View Calendar')}
      </Button>
    </div>
  );
};

const UserTurnCard = ({ turn }: Omit<TurnCardProps, 'turn'> & { turn: TranscriptTurn }) => {
  return (
    <div css={turnContentStyle}>
      <Avatar size='medium' />
      <Text>{turn.text}</Text>
    </div>
  );
};

export const ConversationTranscript = ({
  transcripts,
  userInitials,
  userName,
  isAISpeaking = false,
  isUserSpeaking = false,
  showYourTurn = false,
  callEnded = false,
  settingsChangedBanner = null,
  onViewCalendar,
}: ConversationTranscriptProps) => {
  const scrollRef = useRef<HTMLDivElement>(null);

  // Auto-scroll to bottom when new transcripts arrive or speaking state changes
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [transcripts, isAISpeaking, isUserSpeaking, showYourTurn, callEnded, settingsChangedBanner]);

  return (
    <div css={containerStyle} ref={scrollRef}>
      {transcripts.map((turn) =>
        isScheduleEventTurn(turn) ? (
          <ScheduleEventCard key={turn.id} turn={turn} onViewCalendar={onViewCalendar} />
        ) : turn.speaker === 'ai' ? (
          <AITurnCard key={turn.id} turn={turn} />
        ) : (
          <UserTurnCard key={turn.id} turn={turn} userInitials={userInitials} userName={userName} />
        )
      )}
      {isAISpeaking && <AITypingIndicator />}
      {!isAISpeaking && !isUserSpeaking && showYourTurn && <YourTurnIndicator />}
      {isUserSpeaking && <UserTypingIndicator />}
      {callEnded && <CallEndedIndicator />}
      {settingsChangedBanner && <BannerNotification status='info' message={settingsChangedBanner} />}
    </div>
  );
};
